
import React from 'react';
import {connect} from 'react-redux';
import {getFormSyncWarnings, getFormValues, reduxForm} from 'redux-form';
import {HttpStatusCode} from 'axios';
import PropTypes from 'prop-types';
import {addClass, isEmpty, removeClass} from '@grapecity/wijmo';
import {CellType} from '@grapecity/wijmo.grid';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import Cancel from '@mui/icons-material/Cancel';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Divider from '@mui/material/Divider';

import PaperPart from '../../../atoms/PaperPart.js';
import {PositiveButton} from '../../../atoms/Buttons/PositiveButton.js';
import DangerButton from '../../../atoms/Buttons/DangerButton.js';
import {withMediaQuery} from '../../../atoms/withMediaQuery.js';
import {postRequest} from '../../../../common/backend.js';
import {CATEGORY_TYPE, GMN, GMN_PATH, SURVEY_INDUSTRY_STATUS,
  STEP_APPROVAL_STATUS, CORP_CD, SURVEY_MANAGEMENT_CATEGORY_CODE,
  OPERATION_PERMISSION_TYPE,
  NW_OFFICE_CODE} from '../../../../common/commonConst.js';
import {createFieldItems, createGridFields, createDataMap,
  createDataMapNoBlank, sysErr} from '../../../../common/commonGmn.js';
import {getComboName, createMessageLabel, formatRowIndex, formatDate,
  getNewNwOfficeMap, getOldNwOfficeMap, setComboList,
  formatStringDate,
  ISO_LOCAL_DATE_TIME} from '../../../../common/common.js';
import {requiredCheck, lengthCheck, dateCheck, filterErrorMessage,
  yearIntegerCheck, errorResult} from '../../../../common/inputCheck.js';
import {getSessionObject} from '../../../../common/Session.js';
import CustomBreadcrumbs from '../../../molecules/Breadcrumbs.js';
import CustomFlexGrid, {createComponents} from '../../../molecules/CustomFlexGrid.js';
import WarningMessageArea from '../../../molecules/WarningMessageArea.js';
import ApprovalStatus from '../../../organisms/Approval/ApprovalStatus/index.js';
import ApprovalComment from '../../../organisms/Approval/ApprovalComment/index.js';
import MainContainer from '../../../organisms/MainContainer.js';
import withRouter from '../../../organisms/withRouter.js';
import withFormatMessage from '../../../organisms/withFormatMessage.js';
import {commonOperations} from '../../../../reducks/common/index.js';
import SCZ1012 from '../../SCZ/SCZ1012';

// 申請情報Items
const gmnItemsOrder = {
  OrderNo: {binding: 'orderNo', label: '申請番号', required: false, type: 'text', sm: 2, readOnly: true},
  SurveyCompanyCode: {binding: 'surveyCompanyCode', label: '', required: true, type: 'select', sm: 2, readOnly: false},
  NwOfficeCode: {binding: 'nwOfficeCode', label: 'NW事業所', required: true, type: 'select', sm: 2, readOnly: false},
  IssuerUserName: {binding: 'issuerUserName', label: '作成者', required: false, type: 'text', sm: 2, readOnly: true},
  CreateDate: {binding: 'createDate', label: '作成日', required: true, type: 'date', sm: 2, variant: 'outlined', readOnly: false, blank: true},
  ApprovalStatus: {binding: 'approvalStatus', label: '', required: false, type: 'text', sm: 1, readOnly: true},
  ApprovalStatusName: {binding: 'approvalStatusName', label: 'ステータス', required: false, type: 'text', sm: 1, readOnly: true},
  SurveyCheckInputformId: {binding: 'surveyCheckInputformId', label: '', required: false, type: 'text', sm: 1, readOnly: true},
  UpdateTime: {binding: 'updateTime', label: '', required: false, type: 'date', sm: 1, readOnly: true},
  OrderFiscalYear: {binding: 'orderFiscalYear', label: '', required: false, type: 'text', sm: 1, readOnly: true},
  BasisYear: {binding: 'basisYear', label: '', required: false, type: 'text', sm: 1, readOnly: true},
};

// 承認ステータスItems
const gmnItemsStepApproval = {
  StepApprovalComment: {binding: 'stepApprovalComment', label: '否決コメント', required: false, type: 'text', sm: 6, variant: 'outlined', multiline: true, rows: 3, readOnly: true},
  StepApprovalDateTime: {binding: 'stepApprovalDateTime', label: '否決日', required: false, type: 'text', sm: 2, readOnly: true},
  StepApprovalStatus: {binding: 'stepApprovalStatus', label: '', required: false, type: 'text', sm: 2, readOnly: true},
  Approvable: {binding: 'approvable', label: '', required: false, type: 'text', sm: 2, readOnly: true},
  WfId: {binding: 'wfId', label: '', required: false, type: 'text', sm: 2, readOnly: true},
  ApprovalRouteId: {binding: 'approvalRouteId', label: '', required: false, type: 'text', sm: 2, readOnly: true},
  ApprovalStepId: {binding: 'approvalStepId', label: '', required: false, type: 'text', sm: 2, readOnly: true},
};

// 調査業種登録情報Items
const gridItems = [
  {binding: 'oldNwOfficeCode', header: '旧NW事業所', dataType: 'String', dataMap: null, width: 120, isReadOnly: true},
  {binding: 'blockNo', header: '区画番号', dataType: 'String', width: 100, isReadOnly: true},
  {binding: 'blockNoNextSurveyFiscalYear', header: '区画次回調査年度', dataType: 'String', width: 100, isReadOnly: true},
  {binding: 'ciKkyTtnTtNo', header: '供給地点特定番号', dataType: 'String', width: 220, isReadOnly: true},
  {binding: 'juJysKngMei', header: 'お客さま名', dataType: 'String', width: 200, isReadOnly: true},
  {binding: 'surveyManagementCategoryCode', header: '調査管理区分', dataType: 'String', dataMap: null, width: 130, isReadOnly: false},
  {binding: 'surveyIndustryCode', header: '調査業種', dataType: 'String', dataMap: null, width: 130, isReadOnly: false},
  {binding: 'nextSurveyFiscalYear', header: '次回調査年度', dataType: 'String', required: true, width: 90, isReadOnly: false, maxLength: 4},
  {binding: 'kyCpsSvmCd', header: 'サービスメニュー', dataType: 'String', dataMap: null, width: 200, isReadOnly: true},
  {binding: 'remark', header: '備考', dataType: 'String', width: 300, isReadOnly: false, maxLength: 50},

  {binding: 'surveyIndustryTransferDetailId', header: '調査業種異動明細ID', dataType: 'Number', visible: false, isReadOnly: true},
  {binding: 'transferDetailUpdateTime', header: '調査業種異動明細更新日時', dataType: 'String', visible: false, isReadOnly: true},
  {binding: 'surveyCheckCustomerId', header: '調査点検お客さまID', dataType: 'Number', visible: false, isReadOnly: true},
  {binding: 'customerUpdateTime', header: '調査点検お客さま更新日時', dataType: 'String', visible: false, isReadOnly: true},
  {binding: 'lastSurveyFiscalYear', header: '前回調査年度', dataType: 'String', visible: false, isReadOnly: true},
  {binding: 'cmNextSurveyFiscalYear', header: '調査点検お客さまの次回調査年度', dataType: 'String', visible: false, isReadOnly: true},
  {binding: 'orderVersion', header: '申請バージョン', dataType: 'String', visible: false, isReadOnly: true},
  {binding: 'gridStatus', header: '', dataType: 'String', visible: false, isReadOnly: true},
];

/**
 * 調査業種登録
 * 画面ID:SCA1002
 */
class Container extends React.Component {
  constructor(props) {
    super(props);

    const storage = getSessionObject(GMN.SURVEY_INDUSTRY_INFO.id);
    let list = [].concat(
      storage?.transitionList != null ? storage.transitionList : []);
    list.push({gmnName: GMN.SURVEY_INDUSTRY_INFO.name, href: ''});

    this.isVisible = props.menuItems.some(
        (item) => item.gamenId === GMN.SURVEY_INDUSTRY_INFO.gmnId &&
          item.operationPermissionType ===
              OPERATION_PERMISSION_TYPE.EXECUTE);    // 実行権限

    this.state = {
      transitionList: list,
      isDisabled: true,
      warningMessages: [],
      // 調査点検入力票情報
      inputformInfo: {
        orderNo: storage?.orderNo,
        nwOfficeCode: storage?.nwOfficeCode,
        surveyCompanyCode: storage?.surveyCompanyCode,
      },
      buttonDisabled: true,
      btnApplyDisabled: true,
      modalOpen: false,
      // 承認機能部表示判定条件
      showStepApproval: false,
      showBtnApply: false,
      showBtnApproval: false,
      showApprovalModal: false,
      showApprovalCommentModal: false,
    };
    // 一覧Ref作成
    this.gridRef = React.createRef();
    this.okSelected = false;

    this.gmnItemsOrderList = createFieldItems(gmnItemsOrder);
    this.gmnItemsStepApprovalList = createFieldItems(gmnItemsStepApproval);
    this.setComboItems();
    this.setGridItems();
  }

  /**
   * コンボボックス項目設定
   */
  setComboItems = () => {
    const {genericMap, userInfo} = this.props;

    // NW事業所
    this.nwOfficeCodes = userInfo.authNwOfficeCodes; // ユーザの権限よりNW事業所を取得する
    let nwOfficeList = getNewNwOfficeMap(genericMap, this.nwOfficeCodes);

    // NW事業所
    setComboList(
        this.gmnItemsOrderList,
        gmnItemsOrder.NwOfficeCode.label,
        nwOfficeList, true);
  };

  /**
   * 画面項目設定
   */
  setGridItems = () => {
    const {genericMap} = this.props;
    let views = gridItems;
    let data = null;

    views.forEach((view) => {
      switch (view.binding) {
        case 'oldNwOfficeCode':
          data = createDataMap(genericMap, CATEGORY_TYPE.NW_OFFICE_CODE);
          view.dataMap = data;
          break;

        case 'surveyManagementCategoryCode':
          data = createDataMapNoBlank(genericMap,
              CATEGORY_TYPE.SURVEY_MANAGEMENT_CATEGORY_CODE);
          view.dataMap = data;
          break;

        case 'surveyIndustryCode':
          data = createDataMap(genericMap, CATEGORY_TYPE.SURVEY_INDUSTRY_CODE);
          view.dataMap = data;
          break;

        case 'kyCpsSvmCd':
          data = createDataMap(genericMap, CATEGORY_TYPE.KY_CPS_SVM_CD);
          view.dataMap = data;
          break;

        default:
          break;
      }
    });
  };

  /**
   * 初期処理
   */
  async componentDidMount() {
    try {
      await this.getInitializeData();
    } catch (error) {
      sysErr(this.props);
    }
  }

  /**
   * 終了処理
   */
  async componentWillUnmount() {
    this.props.reset();
    this.props.doSetInitValues(null);
  }

  /**
   * 初期データ検索
   */
  getInitializeData = async () => {
    let _warningMessages = [];
    let _inputformInfo = {...this.state.inputformInfo};
    try {
      this.props.doSetInitValues(null);
      let initItems = this.initializeItems();

      if (isEmpty(_inputformInfo.orderNo)) {
        _inputformInfo.surveyCompanyCode = initItems.surveyCompanyCode;
        _inputformInfo.nwOfficeCode = initItems.nwOfficeCode;
      }

      let _body = {
        inputformData: _inputformInfo,
      };

      // リクエスト出す
      let response = await postRequest('sca1002/search-survey-industry-regist', _body);
      if (!response || response.status != HttpStatusCode.Ok) {
        if (response.data?.message != null) {
          this.props.doShowMessage({
            message: {
              id: response.data?.message.id,
              text: response.data?.message.text,
            },
          });
        } else {
          this.props.doShowMessage({
            message: {id: 'TKSCS00011E', values: ['調査業種登録情報', '取得']},
          });
        }
        return;
      }

      // 画面項目データ設定
      this.setInitializeData(response.data, _inputformInfo, initItems);

      if (initItems[gmnItemsOrder.ApprovalStatus.binding] ==
          SURVEY_INDUSTRY_STATUS.CREATING) {
        _warningMessages = this.inputCheck(
            initItems, response.data?.registList);
      }
    } catch (error) {
      sysErr(this.props);
    } finally {
      this.setState({
        inputformInfo: _inputformInfo,
        warningMessages: _warningMessages,
      });
    }
  };

  /**
   * 画面項目データ設定
   * @param {Object} responseData
   * @param {Object} inputformInfo
   * @param {Object} initItems
   */
  setInitializeData = (responseData, inputformInfo, initItems) => {
    const {genericMap} = this.props;

    let registList = responseData?.registList || [];
    let inputformData = responseData?.inputformData || {};
    let approvalResponse = responseData?.approvalResponse || {};

    // 取得した調査点検入力票情報を画面項目の初期値に設定
    for (const key of Object.keys(initItems)) {
      if (Object.hasOwn(inputformData, key) && inputformData[key] != null) {
        initItems[key] = inputformData[key];
      }
    }

    // NW事業所を設定
    if (initItems[gmnItemsOrder.ApprovalStatus.binding] !==
        SURVEY_INDUSTRY_STATUS.CREATING) {
      let nwOfficeList = [{
        cd: initItems.nwOfficeCode,
        cdNameAbbr: getComboName(genericMap,
            CATEGORY_TYPE.NW_OFFICE_CODE,
            initItems.nwOfficeCode),
      }];
      setComboList(
          this.gmnItemsOrderList,
          gmnItemsOrder.NwOfficeCode.label,
          nwOfficeList, true);
    } else {
      if (!isEmpty(initItems.nwOfficeCode) &&
          initItems.nwOfficeCode !== NW_OFFICE_CODE.CHUDEN_NW &&
          !this.nwOfficeCodes.includes(initItems.nwOfficeCode)) {
        this.props.doShowMessage({
          message: {id: 'TKSCS00049E'},
        });
      }
    }

    // 承認ステータスを設定
    initItems[gmnItemsOrder.ApprovalStatusName.binding] =
      getComboName(genericMap,
          CATEGORY_TYPE.SURVEY_INDUSTRY_STATUS,
          initItems.approvalStatus);

    // 承認状況データを設定
    this.setApprovalData(approvalResponse, initItems);

    // 調査業種登録情報データを画面に設定
    this.props.doSetInitValues(initItems);

    if (this.gridRef?.current) {
      // 変更前リストデータを画面に設定
      this.gridRef.current.setItemsSource(registList);
    }

    // 調査点検入力票情報設定
    inputformInfo.orderNo = inputformData.orderNo;
    inputformInfo.basisYear = inputformData.basisYear;

    // 画面項目プロパティ設定
    this.setItemProps(initItems);

    this.setState({
      btnApplyDisabled: !(registList && registList.length > 0),
    });
  };

  /**
   * 承認状況データ設定
   * @param {Object} approvalResponse
   * @param {Object} initItems
   */
  setApprovalData = (approvalResponse, initItems) => {
    if (isEmpty(approvalResponse)) {
      initItems[gmnItemsStepApproval.StepApprovalStatus.binding] =
          STEP_APPROVAL_STATUS.SHINSEI_MI;
      return;
    }
    // 承認権限
    initItems[gmnItemsStepApproval.Approvable.binding] =
      approvalResponse.approvable;
    // 承認申請状況
    initItems[gmnItemsStepApproval.StepApprovalStatus.binding] =
      approvalResponse.approvalStatus;

    switch (approvalResponse.approvalStatus) {
      case STEP_APPROVAL_STATUS.SHINSEI_MI:
        // 承認申請がまだ、もしくは終わった場合
        // 画面の入力項目は承認申請前の状態で対応する必要がある。
        break;

      case STEP_APPROVAL_STATUS.SHINSEI_TYU:
        // 承認申請が進行中で、上司の承認待ち。
        // 承認WF ID、 承認ルートID、 承認ステップID
        initItems[gmnItemsStepApproval.WfId.binding] = approvalResponse.wfId;
        initItems[gmnItemsStepApproval.ApprovalRouteId.binding] =
          approvalResponse.approvalRouteId;
        initItems[gmnItemsStepApproval.ApprovalStepId.binding] =
          approvalResponse.approvalStepId;
        break;

      case STEP_APPROVAL_STATUS.SHINSEI_SAI:
        // 承認申請が差し戻された or 取下げたため、再度申請。
        initItems[gmnItemsStepApproval.StepApprovalComment.binding] =
          approvalResponse.comment;
        if (!isEmpty(approvalResponse.commentedTime)) {
          initItems[gmnItemsStepApproval.StepApprovalDateTime.binding] =
            formatDate(new Date(approvalResponse.commentedTime));
        }
        break;
      default:
        break;
    }
  };

  /**
   * 画面項目プロパティ設定
   * @param {Object} initItems
   */
  setItemProps = (initItems) => {
    let _isDisabled = true;
    let _showStepApproval = false;
    let _showBtnApply = false;
    let _showBtnApproval = false;

    if (isEmpty(initItems[gmnItemsOrder.ApprovalStatus.binding]) ||
        initItems[gmnItemsOrder.ApprovalStatus.binding] ===
            SURVEY_INDUSTRY_STATUS.CREATING) {
      _isDisabled = !this.isVisible;
    }

    // 承認申請ボタン表示
    if (initItems[gmnItemsOrder.ApprovalStatus.binding] ===
        SURVEY_INDUSTRY_STATUS.CREATING &&
      (initItems[gmnItemsStepApproval.StepApprovalStatus.binding] ===
          STEP_APPROVAL_STATUS.SHINSEI_MI ||
        initItems[gmnItemsStepApproval.StepApprovalStatus.binding] ===
            STEP_APPROVAL_STATUS.SHINSEI_SAI)) {
      _showBtnApply = true;
    }

    // 承認・否決ボタン表示
    if (initItems[gmnItemsStepApproval.Approvable.binding] &&
        ((initItems[gmnItemsOrder.ApprovalStatus.binding] ===
            SURVEY_INDUSTRY_STATUS.APPROVING &&
          initItems[gmnItemsStepApproval.StepApprovalStatus.binding] ===
              STEP_APPROVAL_STATUS.SHINSEI_TYU))) {
      _showBtnApproval = true;
    }

    // 否決（枠）表示
    if ((initItems[gmnItemsOrder.ApprovalStatus.binding] ===
        SURVEY_INDUSTRY_STATUS.CREATING &&
          initItems[gmnItemsStepApproval.StepApprovalStatus.binding] ===
              STEP_APPROVAL_STATUS.SHINSEI_SAI)) {
      _showStepApproval = true;
    }

    this.gmnItemsOrderList.forEach((item) => {
      item.readOnly = _isDisabled;
      switch (item.name) {
        case gmnItemsOrder.CreateDate.binding:
          item.InputProps.readOnly = _isDisabled;
          break;
        case gmnItemsOrder.NwOfficeCode.binding:
          item.InputProps.readOnly = _isDisabled;
          break;
        default:
          break;
      }
    });

    this.setState({
      isDisabled: _isDisabled,
      showBtnApply: _showBtnApply,
      showBtnApproval: _showBtnApproval,
      showStepApproval: _showStepApproval,
    });
  };

  /**
   * 画面項目初期化
   * @return {object} 画面項目
   */
  initializeItems = () => {
    const {userInfo} = this.props;

    const gmnItems = {
      ...gmnItemsOrder,
      ...gmnItemsStepApproval,
    };

    let initItems = {};
    for (const key of Object.keys(gmnItems)) {
      const item = gmnItems[key];
      if (Object.hasOwn(item, 'binding')) {
        initItems[item.binding] = null;
      }
    }

    // Ⅰ期対応
    initItems[gmnItemsOrder.SurveyCompanyCode.binding] = CORP_CD.CBID_USER;
    initItems[gmnItemsOrder.NwOfficeCode.binding] =
      userInfo.nwOfficeCode;
    initItems[gmnItemsOrder.IssuerUserName.binding] = userInfo.userName;
    let sysDate = formatDate(new Date(), 'yyyy-MM-dd');
    initItems[gmnItemsOrder.CreateDate.binding] = sysDate;
    initItems[gmnItemsOrder.ApprovalStatus.binding] =
      SURVEY_INDUSTRY_STATUS.CREATING;

    return initItems;
  };

  /**
   * 変更前一覧描画
   * @return {*}
   */
  renderTable() {
    let isReadOnly = this.state.isDisabled;
    const props = {
      rowHeaderType: 'check',
      id: 'SCA1002Table',
      filterOn: true,
      headersVisibility: 'Column',
      allowDragging: 'Columns',
      style: {maxHeight: 'auto'},
      isReadOnly: isReadOnly,
      screenId: 'SCA1002',
      doShowMessage: this.props.doShowMessage,
      validateEdits: false,
      DeleteOn: true,
      isDisplayCount: true,
      checkedFunction: this.checkStateUpdate,
      formatItemFunction: this.onFormatItemHandler,
      cellEditEndedFunction: this.onCellEditEndedFunction,
      pastedCellFunction: this.onPastedCellFunction,
      getError: this.getError,
    };

    let views = gridItems;
    const component = createComponents(views, isReadOnly);
    return (
      <CustomFlexGrid ref={this.gridRef} {...props}>
        {component}
      </CustomFlexGrid>
    );
  }

  /**
   * 調査業種登録一覧画面の入力チェック
   * @param {object} item 検証行データ
   * @param {string} prop binding
   * @param {boolean} parsing 予期されたデータ型に解析できない値を入力したかどうか
   * @return {string} 結果
   */
  getError = (item, prop, parsing) => {
    if (parsing) {
      return null;
    }

    const targets = gridItems
        .filter((targetItem) => targetItem.binding == prop);
    const target = targets.length > 0 ? targets[0] : {};

    const label = createMessageLabel(null, target.header);

    let result = this._requiredCheck(target.required, label, item[prop]);
    if (result.error) {
      return this.props.getMessage(result.message.id, result.message.values);
    }

    result = this._lengthCheck(target.maxLength, label, item[prop]);
    if (result.error) {
      return this.props.getMessage(result.message.id, result.message.values);
    }

    if (prop === 'nextSurveyFiscalYear') {
      result = yearIntegerCheck(label, item[prop]);
      if (result.error) {
        return this.props.getMessage(result.message.id, result.message.values);
      }

      // 調査管理区分、調査業種、調査年度チェック
      // 調査管理区分が０か３かAでかつ業種が０２から０９のもは次回調査年度は当該年度＋１年であること。
      if (!isEmpty(item['surveyManagementCategoryCode']) &&
        (item['surveyManagementCategoryCode'] === SURVEY_MANAGEMENT_CATEGORY_CODE.NON_PRIVATE ||
          item['surveyManagementCategoryCode'] === SURVEY_MANAGEMENT_CATEGORY_CODE.LOWPOWER_PRIVATE ||
          item['surveyManagementCategoryCode'] === SURVEY_MANAGEMENT_CATEGORY_CODE.JR_CHECK)
      ) {
        let basisYear = this.state.inputformInfo.basisYear;
        if (!isEmpty(item['surveyIndustryCode']) &&
        item['surveyIndustryCode'] >= '02' && item['surveyIndustryCode'] <= '09' &&
          parseInt(item['nextSurveyFiscalYear']) !== parseInt(basisYear) + 1) {
          return this.props.getMessage('TKSCA00030E', ['当該年度の翌年度', '']);
        }
      }

      // 調査管理区分、調査業種未設定、調査年度チェック
      // 調査管理区分が０か３かAでかつ業種が００（空白、未選択）のものは、次回調査年度は前回調査年度＋４年以内であること。
      if (!isEmpty(item['surveyManagementCategoryCode']) &&
        (item['surveyManagementCategoryCode'] === SURVEY_MANAGEMENT_CATEGORY_CODE.NON_PRIVATE ||
          item['surveyManagementCategoryCode'] === SURVEY_MANAGEMENT_CATEGORY_CODE.LOWPOWER_PRIVATE ||
          item['surveyManagementCategoryCode'] === SURVEY_MANAGEMENT_CATEGORY_CODE.JR_CHECK)
      ) {
        if (isEmpty(item['surveyIndustryCode']) &&
          !isEmpty(item['lastSurveyFiscalYear']) &&
            (parseInt(item['nextSurveyFiscalYear']) > parseInt(item['lastSurveyFiscalYear']) + 4 ||
            parseInt(item['nextSurveyFiscalYear']) <= parseInt(item['lastSurveyFiscalYear']))) {
          return this.props.getMessage('TKSCA00031E', ['前回調査年度の４年度', '']);
        }
      }

      // 初回調査チェック
      // 次回が初回調査(前回調査年度がNULL)となるお客さまの次回調査年度は、翌年度～調査点検お客さまの次回調査年度以内であること。
      if (isEmpty(item['lastSurveyFiscalYear']) && !isEmpty(item['cmNextSurveyFiscalYear'])) {
        let basisYear = this.state.inputformInfo.basisYear;
        if (parseInt(item['nextSurveyFiscalYear']) < parseInt(basisYear) + 1 ||
              parseInt(item['nextSurveyFiscalYear']) > parseInt(item['cmNextSurveyFiscalYear'])) {
          return this.props.getMessage('TKSCA00032E', ['翌年度', 'お客さまの次回調査年度', '']);
        }
      }
    }
  };

  /**
   * 必須チェック
   * @param {*} required
   * @param {*} _header
   * @param {*} _value
   * @param {*} rowIndex
   * @return {*} 結果
   */
  _requiredCheck = (required, _header, _value, rowIndex=' ') => {
    if (required) {
      return requiredCheck(_header, _value, rowIndex);
    }
    return {};
  };

  /**
   * 桁数チェック
   * @param {*} maxLength
   * @param {*} header
   * @param {*} value
   * @param {*} rowIndex
   * @return {*} 結果
   */
  _lengthCheck = (maxLength, header, value, rowIndex=' ') => {
    if (maxLength && maxLength > 0) {
      return lengthCheck(header, value, maxLength, rowIndex);
    }
    return {};
  };

  /**
   * 一覧に変更があるかチェックする
   * @return {bool} true: 変更あり
   */
  doCheckModified = () => {
    let result = false;
    if (this.gridRef.current) {
      result = this.gridRef.current.commitAndCheck();
    }
    return result;
  };

  /**
  * ボタンの活性、非活性化
  */
  checkStateUpdate = () => {
    try {
      let bRet = false;

      if (this.gridRef?.current &&
          this.gridRef.current.selectedItemCount() > 0) {
        bRet = true;
      }

      if (bRet) {
        this.setState({buttonDisabled: false});
      } else {
        this.setState({buttonDisabled: true});
      }
    } catch (error) {
      sysErr(this.props);
    }
  };

  /**
   * セル編集終了時の調査業種登録一覧
   * @param {object} grid グリッド
   * @param {object} event イベント
   */
  onCellEditEndedFunction = (grid, event) => {
    if (event != null) {
      const binding = event.panel.columns[event.col]?.binding;

      if (['surveyManagementCategoryCode', 'surveyIndustryCode'].includes(binding)) {
        const cv = grid.collectionView;
        let editItem = cv.items[event.row];

        cv.beginUpdate();
        cv.editItem(editItem);

        if (editItem.surveyManagementCategoryCode !==
          SURVEY_MANAGEMENT_CATEGORY_CODE.JR_CHECK_REJECT &&
            !isEmpty(editItem.surveyIndustryCode)) {
          let basisYear = this.state.inputformInfo.basisYear;
          editItem.nextSurveyFiscalYear = (parseInt(basisYear) + 1) + '';
        } else {
          editItem.nextSurveyFiscalYear = editItem.cmNextSurveyFiscalYear;
        }

        cv.commitEdit();
        cv.endUpdate();
      }

      const listItems = this.gridRef.current.getItems();
      if (!listItems || listItems.length === 0) {
        this.setState({btnApplyDisabled: true});
      }
    }
  };

  /**
   * セルペースト後の処理
   * @param {object} grid グリッド
   * @param {object} event イベント
   */
  onPastedCellFunction = (grid, event) => {
    // セル編集時と同じ処理を行う
    this.onCellEditEndedFunction(grid, event);
  };

  /**
   * onFormatItemハンドラ
   * @param {FlexGrid} s
   * @param {FormatItemEventArgs} e
   */
  onFormatItemHandler = (s, e) => {
    if (s.cells === e.panel &&
          e.panel.cellType === CellType.Cell) {
      const col = e.getColumn();
      const data = s.rows[e.row].dataItem;
      if (col && (col.binding === 'nextSurveyFiscalYear')) {
        if (!isEmpty(data.surveyIndustryCode) ||
          data.surveyManagementCategoryCode ===
            SURVEY_MANAGEMENT_CATEGORY_CODE.JR_CHECK_REJECT) {
          removeClass(e.cell, 'wj-cell-editable');
          addClass(e.cell, 'wj-state-disabled bf-uneditableCell');
        } else {
          removeClass(e.cell, 'wj-state-disabled bf-uneditableCell');
          addClass(e.cell, 'wj-cell-editable');
        }
      }
    }
  };

  /**
   * 行追加ボタン押下時
   */
  doAdd = () => {
    this.setState({modalOpen: true});
  };

  /**
   * 行削除ボタン押下時
   */
  doDelete = () => {
    let _warningMessages = null;
    try {
      if (!this.gridRef || !this.gridRef.current) {
        return;
      }
      // 未選択チェック
      const count = this.gridRef.current.selectedItemCount();
      if (count === 0) {
        this.props.doShowMessage({
          message: {
            id: 'TKSCS00028E',
            values: ['削除'],
          },
        });
        return;
      }
      const items = this.gridRef.current.selectedItem();
      let cv = this.gridRef.current.grid.collectionView;
      cv.beginUpdate();
      // 削除
      for (let rowIndex = count - 1; rowIndex >= 0; rowIndex --) {
        let removeIndex = items[rowIndex].index;
        cv.removeAt(removeIndex);
      }
      cv.endUpdate();

      const listItems = this.gridRef.current.getItems();
      if (!listItems || listItems.length === 0) {
        this.setState({btnApplyDisabled: true});
      }
    } catch (error) {
      sysErr(this.props);
    } finally {
      this.setState({
        warningMessages: _warningMessages,
      });
    }
  };

  /**
   * お客さま情報検索画面を閉じる
   */
  doClose = () => {
    this.setState({modalOpen: false});
  };

  /**
   * お客さま情報表示内容の取得
   * @param {*} items
   */
  getSelectItems = (items) => {
    this.addRow(items);
    this.setState({modalOpen: false});
  };

  /**
   * 行新規
   * @param {*} newRows 新行
   */
  addRow = (newRows) => {
    let cv = this.gridRef.current.grid.collectionView;
    let _itemsCount = cv.sourceCollection.length;
    let _topIndex = cv.sourceCollection.length;
    if (newRows) {
      _itemsCount = _itemsCount + newRows.length;
    }
    if ((_itemsCount) > 100) {
      this.props.doShowMessage({
        message: {id: 'TKSCS00040E', values: [100]},
      });
      return;
    }

    let oldRows = this.gridRef.current.getItems();
    cv.beginUpdate();
    if (newRows) {
      for (const newRow of newRows) {
        let newRowData = newRow._data;
        // 選択されたお客さまはすでに明細に存在している場合、再追加不要。（追加をスキップする）
        let checkRes = oldRows.filter((oldRow) =>
          oldRow.ciKkyTtnTtNo ===
            newRowData.ciKkyTtnTtNo);
        // エラーとする
        if (checkRes && checkRes.length > 0) {
          continue;
        }

        let _newItem = cv.addNew();
        if (_newItem) {
          let _newRow = {
            oldNwOfficeCode: newRowData.oldNwOfficeCode,
            blockNo: newRowData.blockNo,
            blockNoNextSurveyFiscalYear: newRowData.bnNextSurveyFiscalYear,  // ブロックの次回調査年度
            ciKkyTtnTtNo: newRowData.ciKkyTtnTtNo,
            juJysKngMei: newRowData.juJysKngMei,
            surveyManagementCategoryCode: isEmpty(newRowData.surveyManagementCategoryCode) ? '0' : newRowData.surveyManagementCategoryCode,
            surveyIndustryCode: newRowData.surveyIndustryCode,
            nextSurveyFiscalYear: newRowData.nextSurveyFiscalYear,
            kyCpsSvmCd: newRowData.kyCpsSvmCd,
            surveyCheckCustomerId: newRowData.surveyCheckCustomerId, // 調査点検お客さまID
            customerUpdateTime: newRowData.updateTime, // 調査点検お客さまの更新日時
            lastSurveyFiscalYear: newRowData.lastSurveyFiscalYear,
            cmNextSurveyFiscalYear: newRowData.nextSurveyFiscalYear,
            orderVersion: newRowData.updateTime, //  調査点検お客さまの更新日時
          };

          _newItem = Object.assign(_newItem, _newRow);

          _topIndex = _topIndex + 1;
          _newItem['rowId'] = _topIndex;
          _newItem['rowIndex'] = _topIndex;
          cv.commitNew();
        }
      }
    }
    cv.endUpdate();
    this.gridRef.current.grid.refresh();

    if (_itemsCount > 0) {
      this.setState({btnApplyDisabled: false});
    }
  };

  /**
   * 一時保存ボタン押下時
   */
  doInterimSave = async () => {
    const {values} = this.props;
    let _warningMessages = [];
    let _inputformInfo = {...this.state.inputformInfo};

    try {
      const changeItems = this.gridRef.current.getAllItemsWithState();
      let _registList = [];

      for (const changeItem of changeItems) {
        let _item = {...changeItem};
        if (!_item.hasOwnProperty(['surveyIndustryTransferDetailId'])) {
          _item['gridStatus'] = 'insert';
        }
        _item.transferDetailUpdateTime = formatStringDate(
            _item.transferDetailUpdateTime, ISO_LOCAL_DATE_TIME);
        _item.customerUpdateTime = formatStringDate(
            _item.customerUpdateTime, ISO_LOCAL_DATE_TIME);
        _item.orderVersion = formatStringDate(
            _item.orderVersion, ISO_LOCAL_DATE_TIME);
        _registList.push(_item);
      }

      _warningMessages = this.inputCheck(values, _registList);
      const cantSave = filterErrorMessage(['TKSCS00007E', 'TKSCS00009E'], _warningMessages);
      if (cantSave.length > 0) {
        this.props.doShowMessage({
          message: {id: 'TKSCS00013E'},
        });
        return;
      }

      let _body = {
        inputformData: {...values},
        registList: _registList,
        okSelected: this.okSelected,
      };

      let response = await postRequest('sca1002/save-survey-industry-regist', _body);
      if (response.data?.detailMessages != null) {
        _warningMessages = response.data?.detailMessages;
      }
      if (!response || response.status != HttpStatusCode.Ok) {
        if (response.data?.message != null) {
          if (response.data?.message.id === 'TKSCS00053C') {
            this.props.doShowMessage({
              message: {id: response.data?.message.id,
                text: response.data?.message.text},
              action: async () => {
                this.okSelected = true;
                this.doInterimSave();
              },
            });
          } else {
            this.props.doShowMessage({
              message: {id: response.data?.message.id,
                text: response.data?.message.text},
            });
          }
        } else {
          this.props.doShowMessage({
            message: {id: 'TKSCS00011E', values: ['調査業種登録情報', '保存']},
          });
        }
        return;
      }

      this.okSelected = false;

      let initItems = this.initializeItems();
      // 画面項目データ設定
      this.setInitializeData(response.data, _inputformInfo, initItems);
    } catch (error) {
      sysErr(this.props);
    } finally {
      this.setState({
        warningMessages: _warningMessages,
        inputformInfo: _inputformInfo,
      });
    }
  };

  /**
   * 承認申請ボタン押下時
   */
  doApply = () => {
    try {
      this.props.doShowMessage({
        message: {id: 'TKSCS00002C', values: ['承認申請']},
        action: () => {
          this.doApplyAction();
        },
      });

      return;
    } catch (error) {
      sysErr(this.props);
    }
  };

  /**
   * 承認申請処理
   */
  doApplyAction = async () => {
    const {values} = this.props;
    let _warningMessages = [];
    let _inputformInfo = {...this.state.inputformInfo};

    try {
      const changeItems = this.gridRef.current.getAllItemsWithState();
      let _registList = [];
      for (const changeItem of changeItems) {
        let _item = {...changeItem};
        if (!_item.hasOwnProperty(['surveyIndustryTransferDetailId'])) {
          _item['gridStatus'] = 'insert';
        }
        _item.transferDetailUpdateTime = formatStringDate(
            _item.transferDetailUpdateTime, ISO_LOCAL_DATE_TIME);
        _item.customerUpdateTime = formatStringDate(
            _item.customerUpdateTime, ISO_LOCAL_DATE_TIME);
        _item.orderVersion = formatStringDate(
            _item.orderVersion, ISO_LOCAL_DATE_TIME);
        _registList.push(_item);
      }

      _warningMessages = this.inputCheck(values, _registList);
      if (_warningMessages.length > 0) {
        this.props.doShowMessage({
          message: {id: 'TKSCS00012E', values: ['入力エラーがあるの', '承認申請']},
        });
        return;
      }

      let _body = {
        inputformData: {...values},
        registList: _registList,
        okSelected: this.okSelected,
      };

      let response = await postRequest('sca1002/apply-survey-industry-regist', _body);
      if (response.data?.detailMessages != null) {
        _warningMessages = response.data?.detailMessages;
      }
      if (!response || response.status != HttpStatusCode.Ok) {
        if (response.data?.message != null) {
          if (response.data?.message.id === 'TKSCS00053C') {
            this.props.doShowMessage({
              message: {id: response.data?.message.id,
                text: response.data?.message.text},
              action: async () => {
                this.okSelected = true;
                this.doApplyAction();
              },
            });
          } else {
            this.props.doShowMessage({
              message: {id: response.data?.message.id,
                text: response.data?.message.text},
            });
          }
        } else {
          this.props.doShowMessage({
            message: {id: 'TKSCS00011E', values: ['調査業種登録情報', '承認申請']},
          });
        }
        return;
      }

      this.okSelected = false;

      let initItems = this.initializeItems();
      // 画面項目データ設定
      this.setInitializeData(response.data, _inputformInfo, initItems);

      this.props.doShowMessage({
        message: {
          id: 'TKSCS00004I',
          values: ['承認申請'],
        },
      });
    } catch (error) {
      sysErr(this.props);
    } finally {
      this.setState({
        warningMessages: _warningMessages,
        inputformInfo: _inputformInfo,
      });
    }
  };

  /**
   * 取消ボタン押下時
   */
  doFullDelete = async () => {
    const {values} = this.props;

    try {
      if (values.approvalStatus !== SURVEY_INDUSTRY_STATUS.CREATING) {
        this.props.doShowMessage({
          message: {
            id: 'TKSCS00011E',
            values: ['ステータスが作成中以外の', '取消'],
          },
        });
        return;
      }

      this.props.doShowMessage({
        message: {id: 'TKSCS00002C', values: ['取消']},
        action: () => {
          this.doCancelAction();
        },
      });
    } catch (error) {
      sysErr(this.props);
    }
  };

  /**
   * 取消処理
   */
  doCancelAction = async () => {
    const {values} = this.props;
    let _inputformInfo = {...this.state.inputformInfo};
    try {
      const deleteItems = this.gridRef.current.getAllItemsWithState();
      let _body = {
        inputformData: {...values},
        registList: deleteItems,
      };

      let response = await postRequest('sca1002/cancel-survey-industry-regist', _body);
      if (!response || response.status != HttpStatusCode.Ok) {
        if (response.data?.message != null) {
          this.props.doShowMessage({
            message: {id: response.data?.message.id,
              text: response.data?.message.text},
          });
        } else {
          this.props.doShowMessage({
            message: {id: 'TKSCS00011E', values: ['調査業種登録情報', '取消']},
          });
        }
        return;
      }

      if (response.data?.inputformData) {
        let initItems = this.initializeItems();
        // 画面項目データ設定
        this.setInitializeData(response.data, _inputformInfo, initItems);

        this.props.doShowMessage({
          message: {
            id: 'TKSCS00004I',
            values: ['取消'],
          },
        });
        return;
      }

      this.props.navigate(GMN_PATH.SURVEY_INDUSTRY_LIST);
    } catch (error) {
      sysErr(this.props);
    } finally {
      this.setState({
        inputformInfo: _inputformInfo,
      });
    }
  };

  /**
   * 承認ボタン押下時
   */
  doApproval = () => {
    const {values} = this.props;
    let _warningMessages = [];
    try {
      const changeItems = this.gridRef.current.getAllItemsWithState();
      _warningMessages = this.inputCheck(values, changeItems);
      if (_warningMessages.length > 0) {
        this.props.doShowMessage({
          message: {id: 'TKSCS00012E', values: ['申請エラーがあるの', '承認']},
        });
        return;
      }

      // 承認申請と承認時は年度跨ぎのチェック
      if (values.orderFiscalYear !== values.basisYear) {
        this.props.doShowMessage({
          message: {id: 'TKSCS00052E'},
        });
        return;
      }

      _warningMessages = this.versionCheck(changeItems);
      if (_warningMessages.length > 0) {
        this.props.doShowMessage({
          message: {id: 'TKSCS00051E'},
        });
        return;
      }

      this.displayApprovalComment(false, '1');
      return;
    } catch (error) {
      sysErr(this.props);
    } finally {
      this.setState({
        warningMessages: _warningMessages,
      });
    }
  };

  /**
   * 承認・否決ボタン押下時
   */
  handleApprovalExec = async () => {
    // 承認
    if (this.state.approvalType === '1') {
      this.doApprovalAction();
    } else {
      this.doRemandAction();
    }
  };

  /**
   * 承認処理
   */
  doApprovalAction = async () => {
    const {values} = this.props;
    let _warningMessages = [];
    let _inputformInfo = {...this.state.inputformInfo};

    try {
      // 承認コメントチェック
      if (!isEmpty(values.comment) && values.comment.length > 255) {
        this.props.doShowMessage({
          message: {
            id: 'TKSCS00009E',
            values: ['コメント', ''],
          },
        });
        return;
      }

      const changeItems = this.gridRef.current.getAllItemsWithState();
      let _registList = [];
      for (const changeItem of changeItems) {
        let _item = {...changeItem};

        _item.orderVersion = formatStringDate(
            _item.orderVersion, ISO_LOCAL_DATE_TIME);
        _registList.push(_item);
      }

      let _body = {
        inputformData: {...values},
        registList: _registList,
      };

      let response = await postRequest('sca1002/approval-survey-industry-regist', _body);
      if (response.data?.detailMessages != null) {
        _warningMessages = response.data?.detailMessages;
      }
      if (!response || response.status != HttpStatusCode.Ok) {
        if (response.data?.message != null) {
          this.props.doShowMessage({
            message: {id: response.data?.message.id,
              text: response.data?.message.text},
          });
        } else {
          this.props.doShowMessage({
            message: {id: 'TKSCS00011E', values: ['調査業種登録情報', '承認']},
          });
        }
        return;
      }

      let initItems = this.initializeItems();
      // 画面項目データ設定
      this.setInitializeData(response.data, _inputformInfo, initItems);

      this.props.doShowMessage({
        message: {
          id: 'TKSCS00004I',
          values: ['承認'],
        },
      });
    } catch (error) {
      sysErr(this.props);
    } finally {
      this.setState({
        warningMessages: _warningMessages,
        inputformInfo: _inputformInfo,
      });

      // 承認・否決画面を閉じる
      this.closeApprovalComment();
    }
  };

  /**
   * 否決処理
   */
  doRemandAction = async () => {
    const {values} = this.props;
    let _warningMessages = [];
    let _inputformInfo = {...this.state.inputformInfo};

    try {
      if (isEmpty(values.comment)) {
        this.props.doShowMessage({
          message: {
            id: 'TKSCS00005E',
            values: ['コメント', ''],
          },
        });
        return;
      } else if (values.comment.length > 255) {
        this.props.doShowMessage({
          message: {
            id: 'TKSCS00009E',
            values: ['コメント', ''],
          },
        });
      }

      let _body = {
        inputformData: {...values},
      };

      let response = await postRequest('sca1002/remand-survey-industry-regist', _body);
      if (response.data?.detailMessages != null) {
        _warningMessages = response.data?.detailMessages;
      }
      if (!response || response.status != HttpStatusCode.Ok) {
        if (response.data?.message != null) {
          this.props.doShowMessage({
            message: {id: response.data?.message.id,
              text: response.data?.message.text},
          });
        } else {
          this.props.doShowMessage({
            message: {id: 'TKSCS00011E', values: ['調査業種登録情報', '否決']},
          });
        }
        return;
      }

      let initItems = this.initializeItems();
      // 画面項目データ設定
      this.setInitializeData(response.data, _inputformInfo, initItems);

      this.props.doShowMessage({
        message: {
          id: 'TKSCS00004I',
          values: ['否決'],
        },
      });
    } catch (error) {
      sysErr(this.props);
    } finally {
      this.setState({
        warningMessages: _warningMessages,
        inputformInfo: _inputformInfo,
      });

      // 承認・否決画面を閉じる
      this.closeApprovalComment();
    }
  };

  /**
   * 承認画面の表示
   * @param {object} tstkFlag 調査・点検フラグ
   * @param {object} approvalType 承認・否決区分
   */
  displayApprovalComment = (tstkFlag, approvalType) => {
    this.setState({
      showApprovalCommentModal: true,
      tstkFlag: tstkFlag,
      approvalType: approvalType,
    });
  };

  /**
   * 承認・否決画面を閉じる
  */
  closeApprovalComment = () => {
    this.props.change('comment', '');
    this.setState({
      showApprovalCommentModal: false,
    });
  };

  /**
   * 入力チェック
   * @param {object} inputData 画面入力値
   * @param {array} listData 一覧リスト
   * @return {array} エラーメッセージ
   */
  inputCheck = (inputData, listData) => {
    const {genericMap} = this.props;
    let _warning = [];
    let resultList = [];
    let rowIndex = null;

    if (!inputData && !listData) {
      return _warning;
    }

    // NW事業所
    resultList.push(requiredCheck(
        gmnItemsOrder.NwOfficeCode.label,
        inputData[gmnItemsOrder.NwOfficeCode.binding]));
    // 仕掛かり中の統合前事業所のチェック
    if (!isEmpty(inputData[gmnItemsOrder.NwOfficeCode.binding]) &&
        inputData[gmnItemsOrder.NwOfficeCode.binding] !==
          NW_OFFICE_CODE.CHUDEN_NW &&
        !this.nwOfficeCodes.includes(
            inputData[gmnItemsOrder.NwOfficeCode.binding])) {
      resultList.push(errorResult('TKSCS00049E'));
    }
    // 作成日
    resultList.push(requiredCheck(
        gmnItemsOrder.CreateDate.label,
        inputData[gmnItemsOrder.CreateDate.binding]));
    resultList.push(dateCheck(
        gmnItemsOrder.CreateDate.label,
        inputData[gmnItemsOrder.CreateDate.binding]));
    // 作成日未来日チェック
    if (!isEmpty(inputData[gmnItemsOrder.CreateDate.binding])) {
      const sysDate = formatDate(new Date());
      const createDate = formatDate(
          new Date(inputData[gmnItemsOrder.CreateDate.binding]));
      if (createDate > sysDate) {
        resultList.push(errorResult('TKSCS00045E', [gmnItemsOrder.CreateDate.label, '']));
      }
    }

    if (listData && listData.length > 0) {
      rowIndex = 0;
      let oldNwOfficeCodes = getOldNwOfficeMap(genericMap,
          inputData[gmnItemsOrder.NwOfficeCode.binding]);
      for (const data of listData) {
        if (data.gridStatus === 'delete') {
          continue;
        }

        rowIndex++;
        if (data.rowIndex == null) {
          data.rowIndex = rowIndex;
        }

        for (const gridItem of gridItems) {
          const _binding = gridItem.binding;
          if (!_binding || !Object.hasOwn(data, _binding)) {
            continue;
          }
          const _value = data[_binding];
          const _header = gridItem.header;
          resultList.push(this._requiredCheck(
              gridItem.required, _header, _value, data.rowIndex));

          resultList.push(this._lengthCheck(
              gridItem.maxLength, _header, _value, data.rowIndex));
        }

        let isInNwOffices = oldNwOfficeCodes.map((item) => item.cd).includes(data['oldNwOfficeCode']);
        if (!isEmpty(inputData[gmnItemsOrder.NwOfficeCode.binding]) &&
            !isInNwOffices) {
          resultList.push(errorResult('TKSCS00042E',
              ['旧NW事業所', formatRowIndex(data.rowIndex)]));
        }

        // 調査管理区分が「B：ＪＲ調査拒否」の場合の次回調査年度のチェックなどがない
        if (isEmpty(data['nextSurveyFiscalYear'])) {
          continue;
        }

        // 次回調査年度妥当性チェック
        resultList.push(yearIntegerCheck('次回調査年度', data['nextSurveyFiscalYear'], data.rowIndex));
        // 次回調査年度が数字以外の場合、調査管理区分、調査業種、調査年度チェックをしない
        if (isNaN(data['nextSurveyFiscalYear'])) {
          continue;
        }

        // 調査管理区分、調査業種、調査年度チェック
        // 調査管理区分が０か３かAでかつ業種が０２から０９のもは次回調査年度は当該年度＋１年であること。
        if (!isEmpty(data['surveyManagementCategoryCode']) &&
              (data['surveyManagementCategoryCode'] === SURVEY_MANAGEMENT_CATEGORY_CODE.NON_PRIVATE ||
              data['surveyManagementCategoryCode'] === SURVEY_MANAGEMENT_CATEGORY_CODE.LOWPOWER_PRIVATE ||
              data['surveyManagementCategoryCode'] === SURVEY_MANAGEMENT_CATEGORY_CODE.JR_CHECK)
        ) {
          if (!isEmpty(data['surveyIndustryCode']) &&
              data['surveyIndustryCode'] >= '02' && data['surveyIndustryCode'] <= '09' &&
                parseInt(data['nextSurveyFiscalYear']) !== parseInt(inputData['basisYear']) + 1) {
            resultList.push(errorResult('TKSCA00030E',
                ['当該年度の翌年度', formatRowIndex(data.rowIndex)]));
          }
        }

        // 調査管理区分、調査業種未設定、調査年度チェック
        // 調査管理区分が０か３かAでかつ業種が００（空白、未選択）のものは、次回調査年度は前回調査年度＋４年以内であること。
        if (!isEmpty(data['surveyManagementCategoryCode']) &&
              (data['surveyManagementCategoryCode'] === SURVEY_MANAGEMENT_CATEGORY_CODE.NON_PRIVATE ||
              data['surveyManagementCategoryCode'] === SURVEY_MANAGEMENT_CATEGORY_CODE.LOWPOWER_PRIVATE ||
              data['surveyManagementCategoryCode'] === SURVEY_MANAGEMENT_CATEGORY_CODE.JR_CHECK)
        ) {
          if (isEmpty(data['surveyIndustryCode']) &&
              !isEmpty(data['lastSurveyFiscalYear']) &&
                (parseInt(data['nextSurveyFiscalYear']) > parseInt(data['lastSurveyFiscalYear']) + 4 ||
                parseInt(data['nextSurveyFiscalYear']) <= parseInt(data['lastSurveyFiscalYear']))) {
            resultList.push(errorResult('TKSCA00031E',
                ['前回調査年度の４年度', formatRowIndex(data.rowIndex)]));
          }
        }

        // 初回調査チェック
        // 次回が初回調査(前回調査年度がNULL)となるお客さまの次回調査年度は、翌年度～調査点検お客さまの次回調査年度以内であること。
        if (isEmpty(data['lastSurveyFiscalYear']) && !isEmpty(data['cmNextSurveyFiscalYear'])) {
          if (parseInt(data['nextSurveyFiscalYear']) < parseInt(inputData['basisYear']) + 1 ||
              parseInt(data['nextSurveyFiscalYear']) > parseInt(data['cmNextSurveyFiscalYear'])) {
            resultList.push(errorResult('TKSCA00032E',
                ['翌年度', 'お客さまの次回調査年度', formatRowIndex(data.rowIndex)]));
          }
        }
      }
    }

    for (const result of resultList) {
      if (result.error) {
        _warning.push(result.message);
      }
    }
    return _warning;
  };

  /**
   * バージョンチェック
   * @param {array} listData 一覧リスト
   * @return {array} エラーメッセージ
   */
  versionCheck = (listData) => {
    let _warning = [];
    let resultList = [];

    if (listData && listData.length > 0) {
      for (const data of listData) {
        // 申請バージョンチェック
        if (data['orderVersion'] !== data['customerUpdateTime']) {
          resultList.push(errorResult('TKSCS00050E', [formatRowIndex(data.rowIndex)]));
        }
      }
    }

    for (const result of resultList) {
      if (result.error) {
        _warning.push(result.message);
      }
    }
    return _warning;
  };

  /**
   * フッタボタン
   * @return {JSX.Element}
   */
  renderFooterBtn() {
    const isDisabled = this.state.isDisabled;
    const isShowCancel = !isDisabled &&
      !isEmpty(this.state.inputformInfo.orderNo);

    return (
      <Grid
        container
        justifyContent="center"
        alignItems="flex-start"
        spacing={1}
      >
        {this.isVisible && !isDisabled &&
        <Grid key="keySCA1002InterimSave" item>
          <PositiveButton
            id="btnSCA1002InterimSave"
            onClick={this.doInterimSave}
            variant="contained"
            startIcon={<SaveAltIcon />}
            disabled={this.state.btnApplyDisabled}
          >
            <span>一時保存</span>
          </PositiveButton>
        </Grid>
        }
        {this.isVisible && isShowCancel &&
        <Grid key="keySCA1002FullDelete" item>
          <DangerButton
            id="btnSCA1002FullDelete"
            onClick={this.doFullDelete}
            variant="contained"
            startIcon={<Cancel />}
          >
            <span>取消</span>
          </DangerButton>
        </Grid>
        }
        {this.isVisible && this.state.showBtnApply &&
        <Grid key="keySCA1002Apply" item>
          <PositiveButton
            id="btnSCA1002Apply"
            onClick={this.doApply}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={this.state.btnApplyDisabled}
          >
            <span>承認申請</span>
          </PositiveButton>
        </Grid>
        }
        {this.isVisible && this.state.showBtnApproval &&
        <Grid key="keySCA1002Approval" item>
          <PositiveButton
            id="btnSCA1002Approval"
            onClick={this.doApproval}
            variant="contained"
            startIcon={<CheckCircle />}
          >
            <span>承認</span>
          </PositiveButton>
        </Grid>
        }
        {this.isVisible && this.state.showBtnApproval &&
        <Grid key="keySCA1002Remand" item>
          <DangerButton
            id="btnSCA1002Remand"
            onClick={()=>{
              this.displayApprovalComment(false, '2');
            }}
            variant="contained"
            startIcon={<Cancel />}
          >
            <span>否決</span>
          </DangerButton>
        </Grid>
        }
        <Grid key="keySCA1002ApprovalState" item>
          <PositiveButton
            id="btnSCA1002ApprovalState"
            onClick={this.displayApproval}
            variant="contained"
          >
            <span>承認状況</span>
          </PositiveButton>
        </Grid>
      </Grid>

    );
  };

  /**
   * 承認状況画面の表示
   */
  displayApproval = () => {
    this.setState({
      showApprovalModal: true,
    });
  };

  /**
   * 承認状況画面画面を閉じる
   */
  closeApproval = () => {
    this.setState({
      showApprovalModal: false,
    });
  };

  /**
   * 画面遷移
   * @param {object} path 遷移パス
   */
  doMoveNext = async (path) => {
    if (this.props.dirty || this.doCheckModified()) {
      this.props.doShowMessage({
        message: 'TKSCS00038W',
        action: () => {
          // OKを押すと画面遷移
          this.props.navigate(path);
        },
      });
    } else {
      // 編集されていない場合は確認なしで遷移
      this.props.navigate(path);
    }
  };

  /**
   * 画面描画
   * @return {*}
   */
  render() {
    // フッタボタンを生成
    const footerBtn = this.renderFooterBtn();

    return (
      <form noValidate autoComplete="off" className="form_root">
        <MainContainer
          props={this.props}
          footerBtn={footerBtn}
        >
          <Grid key="keySCA1002Msg" item xs={12}>
            <WarningMessageArea messages={this.state.warningMessages}/>
          </Grid>
          <CustomBreadcrumbs key="keyBreadcrumbs" items={this.state.transitionList} move={this.doMoveNext}/>
          <Grid key="keySCA1002OrderGrid" item xs={12}>
            <PaperPart>
              {createGridFields(GMN.SURVEY_INDUSTRY_INFO.id,
                  this.gmnItemsOrderList)}
            </PaperPart>
            {this.state.showStepApproval &&
            <span>
              <PaperPart>
                <Typography variant="h6" gutterBottom>否決</Typography>
                {createGridFields(GMN.SURVEY_INDUSTRY_INFO.id,
                    this.gmnItemsStepApprovalList)}
              </PaperPart>
            </span>
            }
            <div style={{marginBottom: '20px'}} />
            <Divider variant="middle" />
            <div style={{marginBottom: '20px'}} />
            <Grid container
              justifyContent="flex-start"
              alignItems="flex-start"
            >
              <Grid key="keySCA1002Table" item xs={12}>
                {this.renderTable()}
              </Grid>
            </Grid>
            <Grid
              container
              justifyContent="flex-start"
              alignItems="flex-start"
              spacing={1}
              direction={'row'}
            >
              <Grid key="keySCA1002LineAdd" item>
                <PositiveButton
                  id="btnSCA1002LineAdd"
                  onClick={this.doAdd}
                  variant="contained"
                  startIcon={<AddIcon />}
                  disabled={this.state.isDisabled}
                >
                  <span>行追加</span>
                </PositiveButton>
              </Grid>
              <Grid key="keySCA1002LineDelete" item>
                <DangerButton
                  id="btnSCA1002LineDelete"
                  onClick={this.doDelete}
                  variant="contained"
                  startIcon={<DeleteIcon />}
                  disabled={this.state.buttonDisabled || this.state.isDisabled}
                >
                  <span>行削除</span>
                </DangerButton>
              </Grid>
            </Grid>
          </Grid>

        </MainContainer>
        {this.state.modalOpen &&
        <SCZ1012
          modalOpen={this.state.modalOpen}
          closeButton={this.doClose}
          selectButton={this.getSelectItems}>
        </SCZ1012>
        }
        {this.state.showApprovalModal &&
        <ApprovalStatus
          openState={this.state.showApprovalModal}
          handleClose = {this.closeApproval}/>
        }
        {this.state.showApprovalCommentModal &&
        <ApprovalComment
          openState={this.state.showApprovalCommentModal}
          tstkFlag={this.state.tstkFlag}
          approvalType={this.state.approvalType}
          handleExec={this.handleApprovalExec}
          handleClose ={this.closeApprovalComment}/>
        }
      </form>
    );
  }
}

Container.propTypes = {
  userInfo: PropTypes.object,
  values: PropTypes.object,
  handleSubmit: PropTypes.func,
  doShowMessage: PropTypes.func,
  doSetInitValues: PropTypes.func,
  navigate: PropTypes.func,
  genericMap: PropTypes.object,
  getMessage: PropTypes.func,
  dirty: PropTypes.bool,
  reset: PropTypes.func,
  change: PropTypes.func,
  menuItems: PropTypes.object,
};

const mapStateToProps = (state) => {
  return {
    userInfo: state.auth.userInfo,
    initialValues: state.common.setInitValues,
    genericMap: state.common.generalPurposeMap,
    menuItems: state.auth.menuItems,
  };
};

const mapDispatchToProps = {
  doShowMessage: commonOperations.doShowMessage,
  doSetInitValues: commonOperations.doSetInitValues,
};

const FORM_NAME = 'SCA1002FORM';
Container = reduxForm({
  form: FORM_NAME,
  destroyOnUnmount: false,
  enableReinitialize: true,
})(connect((state) => {
  return {
    values: getFormValues(FORM_NAME)(state),
    syncWarnings: getFormSyncWarnings(FORM_NAME)(state),
  };
})(Container));

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withRouter(withFormatMessage(withMediaQuery()(Container))));
